import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import FilePreviewDialog, { CaseDocRef } from "./FilePreviewDialog";
import { axiosInstance } from "../../utils/networking";
import ThriveModal from "../common/ThriveModal";
import ActionItemEditModal, {
  actionItemDueDate,
} from "./ActionItems/ActionItemEditModal";
import {
  ActionItemAttachmentType,
  ActionItemType,
  defaultActionItem,
} from "../../interfaces";
import ReferralStatusChip, {
  REFERRAL_PIPELINE_STATUSES,
  pipelineStatusLabels,
  isTerminalPipelineStatus,
} from "../common/ReferralStatusChip";
import OutcomesPanel from "./Outcomes/OutcomesPanel";
import { OutcomeRecordType, ReminderType } from "../../interfaces";
import ReminderRow from "../Dashboard/Reminders/ReminderRow";
import ReminderEditModal from "../Dashboard/Reminders/ReminderEditModal";
import dayjs from "dayjs";

// GET /referral/pipeline responds { referrals: [...] }; tolerate a bare array.
const unwrapPipeline = (data: any): any[] =>
  Array.isArray(data?.referrals)
    ? data.referrals
    : Array.isArray(data)
    ? data
    : [];

// Phones show the objective under "Referred To" instead, so the status select
// keeps its room.
const objectiveColumnSx = { display: { xs: "none", sm: "table-cell" } };

// String tab keys (not indexes) so inserting a tab never shifts the others.
type WorkflowTab =
  | "actions"
  | "resource_referrals"
  | "b2b_referrals"
  | "reminders"
  | "outcomes";

// Provider referrals (created from the B2B email flow) are B2B; Resource
// referrals (and legacy rows with no referral_type but no provider either)
// are Resource referrals. Mirrors the split in Dashboard/MyReferrals.tsx.
export const isB2BReferral = (r: any): boolean =>
  r?.referral_type === "Provider" ||
  (!r?.referral_type && Boolean(r?.provider_id));

// Referral ids grouped by kind — Case.tsx derives them from the creating
// activity ("Referral" → resource, "Provider Referral Initiated" → b2b).
export type ReferralIdGroups = { resource: string[]; b2b: string[] };
export const emptyReferralIdGroups: ReferralIdGroups = { resource: [], b2b: [] };

type ClientWorkflowProps = {
  theCase: any;
  firm: any;
  dataFlag: boolean;
  setDataFlag: (flag: boolean) => void;
  // Set from ?action_item= on the case URL (dashboard deep link) — scrolls to
  // and briefly highlights that Action Item.
  focusActionItemId?: string | null;
  // Set from ?reminder= on the case URL (dashboard feed deep link) — scrolls to
  // and briefly highlights that reminder on the Reminders tab.
  focusReminderId?: string | null;
  // Set from ?referral= on the case URL (dashboard feed / My Referrals deep
  // link) — switches to that referral's tab, scrolls to and briefly highlights
  // its pipeline row.
  focusReferralId?: string | null;
  // Referral ids grouped by kind from the creating activity. Live/achieved
  // rows now come straight from GET /referral/pipeline, so only the archived
  // group (referrals whose creating activity was deleted — they are absent
  // from the pipeline data) is read here; referralCards is kept on the prop
  // type because Case.tsx still supplies it.
  referralCards?: ReferralIdGroups;
  archivedReferralCards?: ReferralIdGroups;
  // Renders the full Referral.tsx card for an id — used only by the popup a
  // "Referred To" title opens. Case.tsx owns the modals those cards open.
  renderReferralCard?: (referralId: string) => React.ReactNode;
};

// Unified per-case workflow surface: Action Items (goals + checklists),
// Resource / B2B Referrals (compact pipeline tables with inline status edit) and Outcomes
// (terminal referrals + completed goals). The big Referral.tsx cards remain the
// detail surface for notice/survey actions — this does not rebuild them.
const ClientWorkflow: React.FC<ClientWorkflowProps> = ({
  theCase,
  firm,
  dataFlag,
  setDataFlag,
  focusActionItemId,
  focusReminderId,
  focusReferralId,
  archivedReferralCards = emptyReferralIdGroups,
  renderReferralCard,
}) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<WorkflowTab>("actions");
  const [highlightId, setHighlightId] = useState<string | null>(null);
  // Which id has already been scrolled to, so dataFlag re-fetches do not
  // yank the page back to it.
  const focusedRef = useRef<string | null>(null);
  const [plans, setPlans] = useState<any[]>([]);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [reminders, setReminders] = useState<ReminderType[]>([]);
  // Toggled by refreshReminders so reminder mutations re-fetch just this list.
  const [reminderFlag, setReminderFlag] = useState(false);
  const [showCompletedReminders, setShowCompletedReminders] = useState(false);
  const [addReminderOpen, setAddReminderOpen] = useState(false);
  const [highlightReminderId, setHighlightReminderId] = useState<string | null>(
    null
  );
  const focusedReminderRef = useRef<string | null>(null);
  const [highlightReferralId, setHighlightReferralId] = useState<string | null>(
    null
  );
  const focusedReferralRef = useRef<string | null>(null);
  // DOM id of the deep-link row (reminder or referral) waiting to be scrolled
  // into view once its tab has rendered.
  const [pendingScrollId, setPendingScrollId] = useState<string | null>(null);
  // Expanded state of the per-tab "Achieved"/"Archived" referral drawers, keyed
  // `<kind>-achieved` / `<kind>-archived`. Controlled only so a ?referral= deep
  // link can open the drawer its target lives in; all default collapsed.
  const [openDrawers, setOpenDrawers] = useState<Record<string, boolean>>({});
  const [outcomes, setOutcomes] = useState<OutcomeRecordType[]>([]);
  const [previewDoc, setPreviewDoc] = useState<CaseDocRef | null>(null);
  // Referral whose full Referral.tsx card is shown in the popup (opened by
  // clicking the "Referred To" title in a pipeline table).
  const [openReferralCardId, setOpenReferralCardId] = useState<string | null>(
    null
  );
  // Archived referrals (their creating activity was deleted) are not in the
  // pipeline payload, so each one is fetched by id and cached here.
  const [archivedRows, setArchivedRows] = useState<Record<string, any>>({});
  // Ids already requested — keeps the fetch effect to one call per referral.
  const archivedFetchedRef = useRef<Set<string>>(new Set());
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Completed items are hidden from the Action Items list by default.
  const [showCompleted, setShowCompleted] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<ActionItemType>({
    ...defaultActionItem,
    case_id: theCase?._id,
  });

  useEffect(() => {
    if (!theCase?._id) return;
    axiosInstance
      .get(`/actionPlan?case_id=${theCase._id}`)
      .then((res) => {
        const list = res?.data?.action_plans || [];
        setPlans(Array.isArray(list) ? list : []);
      })
      .catch(() => setPlans([]));
    axiosInstance
      .get(`/referral/pipeline?case_id=${theCase._id}`)
      .then((res) => {
        // Server responds { referrals: [...] } (same shape MyReferrals reads).
        setReferrals(unwrapPipeline(res?.data));
      })
      .catch(() => setReferrals([]));
    axiosInstance
      .get(`/outcomes?case_id=${theCase._id}`)
      .then((res) => {
        setOutcomes(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => setOutcomes([]));
  }, [theCase?._id, dataFlag]);

  // Stable dependency for the archived fetch effect — the prop is a fresh
  // object on every Case.tsx render, the id list is what actually changes.
  const archivedIdsKey = [
    ...archivedReferralCards.resource,
    ...archivedReferralCards.b2b,
  ].join(",");

  // Archived referrals are soft-deleted, so GET /referral/pipeline never
  // returns them: fetch each one by id (same call Referral.tsx makes) and
  // resolve the partner name from its resource/provider the same way, since
  // the raw doc carries no computed "referred_to".
  useEffect(() => {
    const ids = archivedIdsKey.split(",").filter(Boolean);
    ids.forEach((id) => {
      if (archivedFetchedRef.current.has(id)) return;
      archivedFetchedRef.current.add(id);
      axiosInstance
        .get(`/referral?referral_id=${id}`)
        .then(async (res) => {
          const doc = res?.data?.referralContent;
          if (!doc) return;
          let name = doc.referred_to || "";
          if (!name && doc.resource_id) {
            name = await axiosInstance
              .get(`/resources?resource_id=${doc.resource_id}`)
              .then((r) => r?.data?.resource?.orgName || "")
              .catch(() => "");
          }
          if (!name && doc.provider_id) {
            name = await axiosInstance
              .get(`/resources?provider_id=${doc.provider_id}`)
              .then((r) => r?.data?.provider?.orgName || "")
              .catch(() => "");
          }
          setArchivedRows((prev) => ({
            ...prev,
            [id]: {
              _id: id,
              referred_to: name || "Archived referral",
              referral_objective: doc.referral_objective || "",
              pipeline_status: doc.pipeline_status || "referred",
            },
          }));
        })
        .catch(() => {
          // Allow a later render to retry a failed lookup.
          archivedFetchedRef.current.delete(id);
        });
    });
  }, [archivedIdsKey]);

  const refreshReminders = () => setReminderFlag((v) => !v);

  // Reminders are per-user and mutate on their own (complete/snooze/delete),
  // so they get their own fetch keyed on the local reminderFlag as well.
  useEffect(() => {
    if (!theCase?._id) return;
    axiosInstance
      .get(`/reminder?status=all&case_id=${theCase._id}`)
      .then((res) => {
        const list = res?.data?.reminders;
        setReminders(Array.isArray(list) ? list : []);
      })
      .catch(() => setReminders([]));
  }, [theCase?._id, dataFlag, reminderFlag]);

  // Deep link from the dashboard feed: switch to the Action Items tab, scroll
  // the target block into view and flash a highlight. Silent no-op when the
  // item is gone (completed/deleted since the dashboard loaded).
  useEffect(() => {
    if (!focusActionItemId) return;
    if (focusedRef.current === focusActionItemId) return;
    const found = plans.find(
      (p) =>
        String(p._id) === focusActionItemId &&
        (!p.status || (p.status !== "deleted" && p.status !== "temp"))
    );
    if (!found) return;

    setTab("actions");
    if (found.status === "completed") setShowCompleted(true);
    focusedRef.current = focusActionItemId;
    setHighlightId(focusActionItemId);

    const raf = requestAnimationFrame(() => {
      document
        .getElementById(`action-item-${focusActionItemId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    const timer = setTimeout(() => setHighlightId(null), 2500);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [focusActionItemId, plans]);

  // Same deep link, for ?reminder= rows in the dashboard feed. A completed or
  // cancelled target opens the "Completed" section so the link still lands.
  useEffect(() => {
    if (!focusReminderId) return;
    if (focusedReminderRef.current === focusReminderId) return;
    const found = reminders.find((r) => String(r._id) === focusReminderId);
    if (!found) return;

    setTab("reminders");
    if (found.status !== "pending") setShowCompletedReminders(true);
    focusedReminderRef.current = focusReminderId;
    setHighlightReminderId(focusReminderId);
    // Unlike the Action Items tab (mounted by default), the Reminders tab
    // renders only after the setTab above commits, so the scroll itself runs
    // from the effect below once the row exists in the DOM.
    setPendingScrollId(`reminder-${focusReminderId}`);

    const timer = setTimeout(() => setHighlightReminderId(null), 2500);
    return () => clearTimeout(timer);
  }, [focusReminderId, reminders]);

  // Same deep link, for ?referral= rows (dashboard feed / My Referrals). The
  // tab is picked by the shared isB2BReferral rule; a "completed" target opens
  // the Achieved drawer and an archived one (absent from the pipeline payload,
  // kind known from the Case.tsx groups) opens the Archived drawer, so the link
  // still lands. Silent no-op when the referral has no row on these tabs
  // (e.g. "unsuccessful", which only shows under Outcomes).
  useEffect(() => {
    if (!focusReferralId) return;
    if (focusedReferralRef.current === focusReferralId) return;
    const found = referrals.find((r) => String(r._id) === focusReferralId);
    const archivedKind = archivedReferralCards.resource.some(
      (id) => String(id) === focusReferralId
    )
      ? "resource"
      : archivedReferralCards.b2b.some((id) => String(id) === focusReferralId)
      ? "b2b"
      : null;
    if (
      found &&
      isTerminalPipelineStatus(found.pipeline_status) &&
      found.pipeline_status !== "completed"
    )
      return;
    if (!found && !archivedKind) return;

    const kind = found
      ? isB2BReferral(found)
        ? "b2b"
        : "resource"
      : (archivedKind as "resource" | "b2b");
    setTab(kind === "b2b" ? "b2b_referrals" : "resource_referrals");
    const drawer = !found
      ? `${kind}-archived`
      : found.pipeline_status === "completed"
      ? `${kind}-achieved`
      : null;
    if (drawer) setOpenDrawers((prev) => ({ ...prev, [drawer]: true }));
    focusedReferralRef.current = focusReferralId;
    setHighlightReferralId(focusReferralId);
    // Referral tabs are not mounted by default either, so the scroll runs from
    // the effect below once the row exists in the DOM.
    setPendingScrollId(`referral-row-${focusReferralId}`);
    // Also open the full card, as clicking the row's "Referred To" title would;
    // the row is scrolled to behind it so closing the popup lands on it.
    if (renderReferralCard) setOpenReferralCardId(focusReferralId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusReferralId, referrals, archivedIdsKey]);

  // Own effect so a pipeline refetch inside the flash window (which re-runs the
  // effect above into its ref guard) cannot cancel the clear and leave the row
  // highlighted.
  useEffect(() => {
    if (!highlightReferralId) return;
    const timer = setTimeout(() => setHighlightReferralId(null), 2500);
    return () => clearTimeout(timer);
  }, [highlightReferralId]);

  // Runs after every render while a scroll is pending: the row appears once
  // the tab (and, for done reminders or a drawered referral, its section) has
  // rendered. Retries briefly so content settling above the workflow card
  // cannot leave the row off-screen.
  useEffect(() => {
    if (!pendingScrollId) return;
    const id = pendingScrollId;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setPendingScrollId(null);
        return;
      }
      if (++attempts < 20) timer = setTimeout(tryScroll, 100);
    };
    const raf = requestAnimationFrame(tryScroll);
    return () => {
      cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
    };
  }, [
    pendingScrollId,
    tab,
    showCompletedReminders,
    reminders,
    openDrawers,
    referrals,
    archivedRows,
  ]);

  const activePlans = plans.filter(
    (p) => !p.status || (p.status !== "deleted" && p.status !== "temp")
  );
  // Listed by the same due date the dashboard feed sorts on — the earliest
  // incomplete task's due_date — soonest first, with undated items last.
  const goalPlans = activePlans
    .filter(
      (p) =>
        (p.goal && p.goal.trim()) || (Array.isArray(p.tasks) && p.tasks.length)
    )
    .sort((a, b) => {
      const aDue = actionItemDueDate(a);
      const bDue = actionItemDueDate(b);
      const aVal = aDue && aDue.isValid() ? aDue.valueOf() : Infinity;
      const bVal = bDue && bDue.isValid() ? bDue.valueOf() : Infinity;
      return aVal - bVal;
    });
  const openGoalPlans = goalPlans.filter((p) => p.status !== "completed");
  const completedGoalPlans = goalPlans.filter(
    (p) => p.status === "completed"
  );

  const openReferrals = referrals.filter(
    (r) => !isTerminalPipelineStatus(r.pipeline_status)
  );
  // Same split as MyReferrals: a Provider referral is B2B, everything else is
  // a Resource referral (legacy rows without referral_type fall back to
  // whether a provider_id is set).
  const openB2BReferrals = openReferrals.filter(isB2BReferral);
  const openResourceReferrals = openReferrals.filter(
    (r) => !isB2BReferral(r)
  );
  // Pipeline rows that reached "completed" — listed in the "Achieved
  // Referrals" drawer at the bottom of each tab, same table shape as above.
  const achievedReferrals = referrals.filter(
    (r) => r.pipeline_status === "completed"
  );
  const achievedB2BReferrals = achievedReferrals.filter(isB2BReferral);
  const achievedResourceReferrals = achievedReferrals.filter(
    (r) => !isB2BReferral(r)
  );
  // Archived rows, in the order Case.tsx grouped them; a row still being
  // fetched shows as "Loading…" but is already clickable.
  const archivedTableRows = (ids: string[]) =>
    ids.map(
      (id) =>
        archivedRows[String(id)] || { _id: String(id), referred_to: "Loading…" }
    );
  const terminalReferrals = referrals.filter((r) =>
    isTerminalPipelineStatus(r.pipeline_status)
  );
  const completedGoals = activePlans.filter((p) => p.status === "completed");

  // Server returns dueAt ascending; keep that order within each bucket.
  const pendingReminders = reminders.filter((r) => r.status === "pending");
  const doneReminders = reminders.filter((r) => r.status !== "pending");

  const toggleTask = (plan: any, task: any) => {
    const next = !task.completed;
    // optimistic
    setPlans((prev) =>
      prev.map((p) =>
        p._id === plan._id
          ? {
              ...p,
              tasks: (p.tasks || []).map((t: any) =>
                t.task_id === task.task_id ? { ...t, completed: next } : t
              ),
            }
          : p
      )
    );
    axiosInstance
      .post(`/actionPlan/task`, {
        action_plan_id: plan._id,
        task_id: task.task_id,
        completed: next,
      })
      .then(() => setDataFlag(!dataFlag))
      .catch(() => setDataFlag(!dataFlag));
  };

  const updateReferralStatus = (referral: any, status: string) => {
    axiosInstance
      .post(`/referral/status`, {
        referral_id: referral._id,
        pipeline_status: status,
      })
      .then(() => setDataFlag(!dataFlag))
      .catch(() => {});
  };

  // One table implementation for every referral list on these tabs: open
  // pipeline rows, achieved (completed) rows and archived rows. Archived
  // referrals are deleted, so their status is a read-only chip — the popup
  // card is where "Assign Back" lives.
  const renderReferralTable = (
    rows: any[],
    emptyText: string,
    options: { readOnlyStatus?: boolean } = {}
  ) => (
    <Box>
      {rows.length === 0 ? (
        <Typography sx={{ color: "text.secondary" }}>{emptyText}</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Referred To</TableCell>
              <TableCell sx={objectiveColumnSx}>Objective</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow
                key={r._id}
                id={`referral-row-${String(r._id)}`}
                sx={{
                  transition: "background-color 0.4s",
                  // Same magenta flash the action-item/reminder deep links use,
                  // tinted on the cells since a <tr> cannot carry it.
                  ...(highlightReferralId === String(r._id)
                    ? { "& td": { backgroundColor: "rgba(158,31,99,0.15)" } }
                    : {}),
                }}
              >
                <TableCell>
                  {renderReferralCard ? (
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => setOpenReferralCardId(String(r._id))}
                      sx={{
                        fontSize: "inherit",
                        fontWeight: 500,
                        textAlign: "left",
                        padding: 0,
                      }}
                    >
                      {r.referred_to || "Untitled referral"}
                    </Link>
                  ) : (
                    r.referred_to || "—"
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      display: { xs: "block", sm: "none" },
                      color: "text.secondary",
                    }}
                  >
                    {r.referral_objective || "—"}
                  </Typography>
                </TableCell>
                <TableCell sx={objectiveColumnSx}>
                  {r.referral_objective || "—"}
                </TableCell>
                <TableCell>
                  {options.readOnlyStatus ? (
                    r.pipeline_status ? (
                      <ReferralStatusChip status={r.pipeline_status} />
                    ) : (
                      "—"
                    )
                  ) : (
                    <Select
                      size="small"
                      value={r.pipeline_status || "referred"}
                      onChange={(e) => updateReferralStatus(r, e.target.value)}
                      renderValue={(v) => <ReferralStatusChip status={v} />}
                    >
                      {REFERRAL_PIPELINE_STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>
                          {pipelineStatusLabels[s]}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );

  // Adding a referral navigates to the partner directory rather than opening a
  // picker here: /dashboard/resources and /dashboard/providers are the
  // established referral surfaces (search, tags, factsheets, history) and they
  // already own the whole ?case_id= flow end to end — they resolve the case's
  // firm, confirm the referral, POST /referral and navigate back to
  // /case?id=<case>. A modal would be a second, thinner copy of that.
  const addReferralUrl = (kind: "resource" | "b2b") =>
    kind === "b2b"
      ? `/dashboard/providers?case_id=${theCase?._id}`
      : `/dashboard/resources?case_id=${theCase?._id}`;

  // Pipeline table on top (its titles open the full Referral.tsx card in a
  // popup), then the add button — it sits above the drawers so the empty state
  // is still a way in — then two collapsed drawers holding the same compact
  // rows: Achieved (pipeline status "completed") and Archived (referrals whose
  // creating activity was deleted).
  const renderReferralTab = (
    kind: "resource" | "b2b",
    rows: any[],
    emptyText: string,
    achievedRows: any[],
    archivedIds: string[],
  ) => (
    <Box>
      {renderReferralTable(rows, emptyText)}
      {/* Closed cases do not gain referrals — same rule the case flow cards
          follow in Case.tsx. */}
      {theCase?.status === "open" && (
        <Stack direction="row" spacing={1} sx={{ marginTop: "8px" }}>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => navigate(addReferralUrl(kind))}
          >
            {kind === "b2b" ? "Add B2B Referral" : "Add Resource Referral"}
          </Button>
        </Stack>
      )}
      {achievedRows.length > 0 && (
        <Accordion
          sx={{ marginTop: "16px" }}
          expanded={!!openDrawers[`${kind}-achieved`]}
          onChange={(_, open) =>
            setOpenDrawers((prev) => ({ ...prev, [`${kind}-achieved`]: open }))
          }
        >
          <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
            <Typography component="span">
              Achieved Referrals ({achievedRows.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderReferralTable(achievedRows, "")}
          </AccordionDetails>
        </Accordion>
      )}
      {archivedIds.length > 0 && (
        <Accordion
          sx={{ marginTop: "8px" }}
          expanded={!!openDrawers[`${kind}-archived`]}
          onChange={(_, open) =>
            setOpenDrawers((prev) => ({ ...prev, [`${kind}-archived`]: open }))
          }
        >
          <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
            <Typography component="span">
              Archived Referrals ({archivedIds.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderReferralTable(archivedTableRows(archivedIds), "", {
              readOnlyStatus: true,
            })}
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );

  const handleAddItem = () => {
    setSelectedPlan({
      ...defaultActionItem,
      case_id: theCase._id,
    });
    setEditOpen(true);
  };

  const openEditItem = (plan: any) => {
    setSelectedPlan({ ...plan, case_id: plan.case_id || theCase._id });
    setEditOpen(true);
  };

  // Attachments ride on the shared per-case upload store: files picked in the
  // modal are uploaded first (so a cancelled edit leaves no orphan bytes), the
  // resulting storage pointers are merged onto the plan, and only after the
  // plan saves are removed files deleted from disk. Any upload failure aborts
  // before the plan write so a partial attachment list is never persisted.
  const persistPlan = async (
    overrides: Partial<ActionItemType> = {},
    pendingFiles: File[] = [],
    removed: ActionItemAttachmentType[] = []
  ) => {
    setSaving(true);
    try {
      const uploaded: ActionItemAttachmentType[] = [];
      for (const file of pendingFiles) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axiosInstance.post(
          `/upload/case-doc?case_id=${theCase._id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        // Register the file as a regular case document so it also shows in
        // the case's attached-files list (CaseFileManager reads "Upload File"
        // activities) and can be deleted/restored from there.
        await axiosInstance.post("/activity/", {
          activity_type: "Upload File",
          case_id: theCase._id,
          activity_note: file.name,
          activity_change: {
            storageName: res.data.storageName,
            originalName: file.name,
            case_id: theCase._id,
            source: "action_item",
          },
        });
        uploaded.push({
          storageName: res.data.storageName,
          originalName: file.name,
          mimeType: file.type || null,
          size: file.size,
        });
      }

      const removedNames = new Set(removed.map((a) => a.storageName));
      const attachments = [
        ...(selectedPlan.attachments ?? []).filter(
          (a) => !removedNames.has(a.storageName)
        ),
        ...uploaded,
      ];

      await axiosInstance.post(`/actionPlan`, {
        activity_type: "Action Item",
        action_plan: { ...selectedPlan, ...overrides, attachments },
      });

      // Best-effort cleanup — mirror CaseFileManager's soft delete so the
      // file moves to the case's Archived list (restorable) instead of being
      // unlinked from disk. Files with no "Upload File" activity (uploaded
      // before attachments were registered as case files) are removed for real.
      if (removed.length) await softDeleteCaseFiles(removed).catch(() => {});

      setEditOpen(false);
      setDataFlag(!dataFlag);
    } catch (err) {
      console.error("Action Item save failed", err);
    } finally {
      setSaving(false);
    }
  };

  const softDeleteCaseFiles = async (removed: ActionItemAttachmentType[]) => {
    const res = await axiosInstance.get(`/activity?case_id=${theCase._id}`);
    const activities: any[] = Array.isArray(res?.data?.activity)
      ? res.data.activity
      : [];
    for (const a of removed) {
      const uploadActivity = activities.find(
        (act) =>
          act.activity_type === "Upload File" &&
          act.status !== "deleted" &&
          act.activity_change?.storageName === a.storageName
      );
      if (!uploadActivity) {
        await axiosInstance
          .delete(`/upload/case-doc/${theCase._id}/${a.storageName}`)
          .catch(() => {});
        continue;
      }
      const activityId = uploadActivity.activity_id || uploadActivity._id;
      await axiosInstance.delete(`/activity/?id=${activityId}`);
      await axiosInstance.post("/activity/", {
        activity_type: "Delete File",
        case_id: theCase._id,
        activity_note: uploadActivity.activity_note,
        activity_change: {
          ...uploadActivity.activity_change,
          original_activity_id: activityId,
        },
      });
    }
  };

  const GoalBlock = ({ plan }: { plan: any }) => {
    const tasks: any[] = Array.isArray(plan.tasks) ? plan.tasks : [];
    const done = tasks.filter((t) => t.completed).length;
    return (
      <Box
        id={`action-item-${plan._id}`}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          padding: "12px",
          marginBottom: "12px",
          transition: "box-shadow 0.4s, border-color 0.4s",
          ...(plan.status === "completed"
            ? { backgroundColor: "#fafafa", opacity: 0.85 }
            : {}),
          ...(highlightId === String(plan._id)
            ? {
                borderColor: "#9E1F63",
                boxShadow: "0 0 0 3px rgba(158,31,99,0.25)",
              }
            : {}),
        }}
      >
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {plan.status === "completed" && (
              <Chip label="Completed" color="success" size="small" />
            )}
            <Typography sx={{ fontWeight: "bold" }}>
              {plan.goal || "Goal"}
            </Typography>
          </Stack>
          <Button size="small" onClick={() => openEditItem(plan)}>
            Edit
          </Button>
        </Stack>
        {tasks.length > 0 && (
          <>
            <LinearProgress
              variant="determinate"
              value={tasks.length ? (done / tasks.length) * 100 : 0}
              sx={{ height: 8, borderRadius: 4, marginY: "8px" }}
            />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {done} of {tasks.length} tasks
            </Typography>
            <Stack sx={{ marginTop: "4px" }}>
              {tasks.map((task) => (
                <Stack
                  key={task.task_id}
                  direction="row"
                  sx={{ alignItems: "center" }}
                >
                  <Checkbox
                    size="small"
                    checked={!!task.completed}
                    onChange={() => toggleTask(plan, task)}
                  />
                  <Typography
                    sx={{
                      textDecoration: task.completed ? "line-through" : "none",
                      color: task.completed ? "text.disabled" : "text.primary",
                      flexGrow: 1,
                    }}
                  >
                    {task.title}
                  </Typography>
                  {task.due_date && (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      due {dayjs(task.due_date).format("MMM D, YYYY")}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          </>
        )}
        {/* Attachment chips — shown with or without tasks. */}
        {Array.isArray(plan.attachments) && plan.attachments.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            sx={{ mt: 1 }}
          >
            {plan.attachments.map((a: ActionItemAttachmentType) => (
              <Chip
                key={a.storageName}
                size="small"
                variant="outlined"
                icon={<AttachFileIcon />}
                label={a.originalName}
                clickable
                onClick={() =>
                  setPreviewDoc({
                    case_id: plan.case_id || theCase._id,
                    storageName: a.storageName,
                    originalName: a.originalName,
                  })
                }
              />
            ))}
          </Stack>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        maxWidth: "98%",
        margin: "auto",
        marginBottom: "25px",
        borderRadius: "5px",
        boxShadow:
          "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
        padding: "10px 16px",
        textAlign: "left",
      }}
    >
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ marginBottom: 1 }}
      >
        <Tab value="actions" label={`Action Items (${openGoalPlans.length})`} />
        <Tab
          value="resource_referrals"
          label={`Resource Referrals (${openResourceReferrals.length})`}
        />
        <Tab
          value="b2b_referrals"
          label={`B2B Referrals (${openB2BReferrals.length})`}
        />
        <Tab value="reminders" label={`Reminders (${pendingReminders.length})`} />
        <Tab
          value="outcomes"
          label={`Outcomes (${outcomes.length + terminalReferrals.length + completedGoals.length})`}
        />
      </Tabs>

      {/* Action Items */}
      {tab === "actions" && (
        <Box>
          {openGoalPlans.length === 0 ? (
            <Typography sx={{ color: "text.secondary", marginBottom: "8px" }}>
              {completedGoalPlans.length === 0
                ? "No action items yet."
                : "No open action items."}
            </Typography>
          ) : (
            openGoalPlans.map((plan) => (
              <GoalBlock key={plan._id} plan={plan} />
            ))
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={handleAddItem}
            >
              Add Action Item
            </Button>
            {completedGoalPlans.length > 0 && (
              <Button
                variant="text"
                onClick={() => setShowCompleted((v) => !v)}
              >
                {showCompleted
                  ? "Hide Completed"
                  : `Show Completed (${completedGoalPlans.length})`}
              </Button>
            )}
          </Stack>
          {showCompleted && completedGoalPlans.length > 0 && (
            <Box sx={{ marginTop: "16px" }}>
              <Divider sx={{ marginBottom: "12px" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Completed
                </Typography>
              </Divider>
              {completedGoalPlans.map((plan) => (
                <GoalBlock key={plan._id} plan={plan} />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Referrals — compact tables with inline status edit, split by type */}
      {tab === "resource_referrals" &&
        renderReferralTab(
          "resource",
          openResourceReferrals,
          "No open resource referrals.",
          achievedResourceReferrals,
          archivedReferralCards.resource,
        )}
      {tab === "b2b_referrals" &&
        renderReferralTab(
          "b2b",
          openB2BReferrals,
          "No open B2B referrals.",
          achievedB2BReferrals,
          archivedReferralCards.b2b,
        )}

      {/* Reminders — per-user, mutated in place by ReminderRow */}
      {tab === "reminders" && (
        <Box>
          {pendingReminders.length === 0 ? (
            <Typography sx={{ color: "text.secondary", marginBottom: "8px" }}>
              {doneReminders.length === 0
                ? "No reminders for this case."
                : "No pending reminders."}
            </Typography>
          ) : (
            pendingReminders.map((r) => (
              <Box
                key={String(r._id)}
                id={`reminder-${String(r._id)}`}
                sx={{
                  border: "1px solid transparent",
                  borderRadius: "8px",
                  transition: "box-shadow 0.4s, border-color 0.4s",
                  ...(highlightReminderId === String(r._id)
                    ? {
                        borderColor: "#9E1F63",
                        boxShadow: "0 0 0 3px rgba(158,31,99,0.25)",
                      }
                    : {}),
                }}
              >
                <ReminderRow reminder={r} onChanged={refreshReminders} />
              </Box>
            ))
          )}
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ marginTop: "8px" }}
          >
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() => setAddReminderOpen(true)}
            >
              Add Reminder
            </Button>
            {doneReminders.length > 0 && (
              <Button
                variant="text"
                onClick={() => setShowCompletedReminders((v) => !v)}
              >
                {showCompletedReminders
                  ? "Hide Completed"
                  : `Show Completed (${doneReminders.length})`}
              </Button>
            )}
          </Stack>
          {showCompletedReminders && doneReminders.length > 0 && (
            <Box sx={{ marginTop: "16px" }}>
              <Divider sx={{ marginBottom: "12px" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Completed
                </Typography>
              </Divider>
              {doneReminders.map((r) => (
                <Box
                  key={String(r._id)}
                  id={`reminder-${String(r._id)}`}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "12px",
                    marginBottom: "12px",
                    backgroundColor: "#fafafa",
                    opacity: 0.85,
                    transition: "box-shadow 0.4s, border-color 0.4s",
                    ...(highlightReminderId === String(r._id)
                      ? {
                          borderColor: "#9E1F63",
                          boxShadow: "0 0 0 3px rgba(158,31,99,0.25)",
                        }
                      : {}),
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center" }}
                  >
                    <Chip
                      label={
                        r.status === "cancelled" ? "Cancelled" : "Completed"
                      }
                      color={r.status === "cancelled" ? "default" : "success"}
                      size="small"
                    />
                    <Typography sx={{ fontWeight: "bold" }}>
                      {r.title}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {dayjs(r.dueAt).format("MMM D, YYYY h:mm A")}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Outcomes */}
      {tab === "outcomes" && (
        <Box>
          <OutcomesPanel
            caseId={theCase?._id}
            firmId={firm?._id || theCase?.firm || ""}
            outcomes={outcomes}
            dataFlag={dataFlag}
            setDataFlag={setDataFlag}
          />
          {terminalReferrals.length === 0 && completedGoals.length === 0 && (
            <Typography sx={{ color: "text.secondary" }}>
              No referral or goal outcomes yet.
            </Typography>
          )}
          {terminalReferrals.map((r) => (
            <Box
              key={r._id}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                padding: "12px",
                marginBottom: "12px",
              }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Typography sx={{ fontWeight: "bold" }}>
                  {r.referred_to || "Referral"}
                </Typography>
                <ReferralStatusChip status={r.pipeline_status} />
              </Stack>
              {r.outcome && (
                <Box sx={{ marginTop: "6px" }}>
                  {r.outcome.assistance_provided && (
                    <Typography variant="body2">
                      Assistance provided: {r.outcome.assistance_provided}
                    </Typography>
                  )}
                  {r.outcome.objective_achieved && (
                    <Typography variant="body2">
                      Objective achieved: {r.outcome.objective_achieved}
                    </Typography>
                  )}
                  {r.outcome.outcome_note && (
                    <Typography variant="body2">
                      {r.outcome.outcome_note}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          ))}
          {completedGoals.map((p) => (
            <Box
              key={p._id}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                padding: "12px",
                marginBottom: "12px",
              }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Chip label="Completed Goal" color="success" size="small" />
                <Typography sx={{ fontWeight: "bold" }}>
                  {p.goal}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Box>
      )}

      <ThriveModal
        open={editOpen}
        setOpen={setEditOpen}
        closeByClick={false}
        width={720}
        height="auto"
        fullScreenOnPhone
        contentComponent={
          <ActionItemEditModal
            setOpen={setEditOpen}
            actionItem={selectedPlan}
            setActionItem={setSelectedPlan}
            handleSave={(files, removed) => persistPlan({}, files, removed)}
            handleComplete={(files, removed) =>
              persistPlan({ status: "completed" }, files, removed)
            }
            handleDelete={() => persistPlan({ status: "deleted" })}
            saving={saving}
          />
        }
      />

      <ReminderEditModal
        open={addReminderOpen}
        onClose={() => setAddReminderOpen(false)}
        onSaved={refreshReminders}
        initial={{
          title: `Follow up: ${firm?.["Company Name"] || ""}`,
          case_id: theCase?._id,
          firm_name: firm?.["Company Name"] || null,
          source: { kind: "manual" },
        }}
      />

      {/* Full Referral.tsx card, opened from a pipeline table title */}
      <ThriveModal
        open={openReferralCardId !== null}
        setOpen={(v: boolean) => {
          if (!v) setOpenReferralCardId(null);
        }}
        closeByClick
        width={1000}
        height="auto"
        contentComponent={
          <Box sx={{ padding: "8px 8px 12px" }}>
            {/* The card carries its own title banner; only a close control here */}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <IconButton
                size="small"
                aria-label="Close referral"
                onClick={() => setOpenReferralCardId(null)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            {renderReferralCard && openReferralCardId && (
              <Box
                sx={{
                  "& > *": { boxShadow: "none", marginBottom: 0 },
                }}
              >
                {renderReferralCard(openReferralCardId)}
              </Box>
            )}
          </Box>
        }
      />

      <FilePreviewDialog doc={previewDoc} onClose={() => setPreviewDoc(null)} />
    </Box>
  );
};

export default ClientWorkflow;
